/**
 * Procedural space-themed item icons drawn to offscreen canvases.
 * Prefers loaded photo sprites for ship modules when available.
 * Returns data-URL strings or public asset paths for inventory UI.
 */

import { getModuleSpriteUrl } from './shipSprites.js';

const iconCache = new Map();

const RARITY_GLOW = {
    common:    '#8899aa',
    uncommon:  '#44cc88',
    rare:      '#44aaff',
    epic:      '#cc66ff',
    legendary: '#ffaa22',
};

/**
 * @param {string} shape  weapon|shield|engine|reactor|hull|sensor|cargo|special|wing|crystal|fuel|scrap|chip|drone|armor
 * @param {string} color  hex
 * @param {string} rarity
 * @param {number} seed
 */
export function getItemIcon(shape, color, rarity = 'common', seed = 0) {
    const key = `${shape}|${color}|${rarity}|${seed}`;
    if (iconCache.has(key)) return iconCache.get(key);

    // Prefer transparent PNG photo sprites for modules + trade goods
    const spriteUrl = getModuleSpriteUrl(shape);
    if (spriteUrl) {
        iconCache.set(key, spriteUrl);
        return spriteUrl;
    }

    const size = 64;
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const ctx = c.getContext('2d');
    const glow = RARITY_GLOW[rarity] || RARITY_GLOW.common;

    // Background plate
    ctx.fillStyle = 'rgba(8,12,28,0.95)';
    roundRect(ctx, 2, 2, size - 4, size - 4, 8);
    ctx.fill();
    ctx.strokeStyle = glow;
    ctx.lineWidth = 2;
    ctx.shadowColor = glow;
    ctx.shadowBlur = 8;
    roundRect(ctx, 2, 2, size - 4, size - 4, 8);
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.save();
    ctx.translate(size / 2, size / 2);
    ctx.fillStyle = color;
    ctx.strokeStyle = '#ffffffcc';
    ctx.lineWidth = 1.5;

    drawShape(ctx, shape, color, seed);

    ctx.restore();

    // Rarity corner pip
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(size - 10, 10, 3.5, 0, Math.PI * 2);
    ctx.fill();

    const url = c.toDataURL('image/png');
    iconCache.set(key, url);
    return url;
}

function drawShape(ctx, shape, color, seed) {
    const r = seeded(seed);
    ctx.fillStyle = color;
    ctx.strokeStyle = lighten(color, 60);
    ctx.lineWidth = 1.6;

    switch (shape) {
        case 'weapon':
            // Blaster / cannon
            ctx.fillStyle = color;
            ctx.fillRect(-4, -18, 8, 28);
            ctx.beginPath();
            ctx.moveTo(-10, 10); ctx.lineTo(10, 10); ctx.lineTo(6, 18); ctx.lineTo(-6, 18);
            ctx.closePath(); ctx.fill();
            ctx.fillStyle = '#fff';
            ctx.fillRect(-2, -22, 4, 8);
            break;
        case 'weapon2':
            // Twin lasers
            ctx.fillStyle = color;
            ctx.fillRect(-12, -16, 5, 26);
            ctx.fillRect(7, -16, 5, 26);
            ctx.fillStyle = lighten(color, 40);
            ctx.fillRect(-11, -20, 3, 6);
            ctx.fillRect(8, -20, 3, 6);
            break;
        case 'shield':
            ctx.beginPath();
            ctx.moveTo(0, -18);
            ctx.bezierCurveTo(16, -12, 16, 8, 0, 18);
            ctx.bezierCurveTo(-16, 8, -16, -12, 0, -18);
            ctx.fill();
            ctx.strokeStyle = '#fff8';
            ctx.stroke();
            ctx.fillStyle = '#fff6';
            ctx.beginPath();
            ctx.arc(0, -2, 5, 0, Math.PI * 2);
            ctx.fill();
            break;
        case 'engine':
            // Thruster nozzle
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.moveTo(-10, -14); ctx.lineTo(10, -14); ctx.lineTo(14, 8); ctx.lineTo(-14, 8);
            ctx.closePath(); ctx.fill();
            ctx.fillStyle = '#ff8844';
            ctx.beginPath();
            ctx.moveTo(-8, 8); ctx.lineTo(8, 8); ctx.lineTo(0, 20);
            ctx.closePath(); ctx.fill();
            ctx.fillStyle = '#ffee88';
            ctx.beginPath();
            ctx.moveTo(-4, 8); ctx.lineTo(4, 8); ctx.lineTo(0, 15);
            ctx.closePath(); ctx.fill();
            break;
        case 'reactor':
            ctx.strokeStyle = color;
            ctx.lineWidth = 2.5;
            ctx.beginPath(); ctx.arc(0, 0, 14, 0, Math.PI * 2); ctx.stroke();
            ctx.beginPath(); ctx.arc(0, 0, 8, 0, Math.PI * 2); ctx.stroke();
            ctx.fillStyle = lighten(color, 50);
            ctx.beginPath(); ctx.arc(0, 0, 4, 0, Math.PI * 2); ctx.fill();
            for (let i = 0; i < 6; i++) {
                const a = (i / 6) * Math.PI * 2 + r() * 0.2;
                ctx.strokeStyle = color;
                ctx.beginPath();
                ctx.moveTo(Math.cos(a) * 8, Math.sin(a) * 8);
                ctx.lineTo(Math.cos(a) * 14, Math.sin(a) * 14);
                ctx.stroke();
            }
            break;
        case 'hull':
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.moveTo(0, -16);
            ctx.lineTo(14, 4);
            ctx.lineTo(8, 16);
            ctx.lineTo(-8, 16);
            ctx.lineTo(-14, 4);
            ctx.closePath();
            ctx.fill();
            ctx.strokeStyle = '#fff6';
            ctx.stroke();
            break;
        case 'sensor':
            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            ctx.beginPath(); ctx.arc(0, 4, 10, Math.PI, 0); ctx.stroke();
            ctx.beginPath(); ctx.arc(0, 4, 16, Math.PI * 1.15, -0.15); ctx.stroke();
            ctx.fillStyle = lighten(color, 40);
            ctx.beginPath(); ctx.arc(0, 4, 4, 0, Math.PI * 2); ctx.fill();
            break;
        case 'cargo':
            ctx.fillStyle = color;
            ctx.fillRect(-14, -10, 28, 20);
            ctx.strokeStyle = '#fff5';
            ctx.strokeRect(-14, -10, 28, 20);
            ctx.fillStyle = '#0004';
            ctx.fillRect(-10, -6, 8, 12);
            ctx.fillRect(2, -6, 8, 12);
            break;
        case 'special':
            // Star gem
            star(ctx, 0, 0, 5, 16, 7);
            ctx.fill();
            ctx.fillStyle = '#fff8';
            star(ctx, 0, 0, 5, 8, 3);
            ctx.fill();
            break;
        case 'wing':
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.moveTo(2, -16); ctx.lineTo(18, 12); ctx.lineTo(2, 8); ctx.closePath();
            ctx.fill();
            ctx.beginPath();
            ctx.moveTo(-2, -16); ctx.lineTo(-18, 12); ctx.lineTo(-2, 8); ctx.closePath();
            ctx.fill();
            break;
        case 'crystal':
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.moveTo(0, -16); ctx.lineTo(10, 0); ctx.lineTo(0, 16); ctx.lineTo(-10, 0);
            ctx.closePath(); ctx.fill();
            ctx.fillStyle = '#fff7';
            ctx.beginPath();
            ctx.moveTo(0, -16); ctx.lineTo(5, -2); ctx.lineTo(0, 2); ctx.lineTo(-3, -4);
            ctx.closePath(); ctx.fill();
            break;
        case 'fuel':
            ctx.fillStyle = color;
            ctx.fillRect(-8, -14, 16, 24);
            ctx.fillStyle = lighten(color, 30);
            ctx.fillRect(-6, -10, 12, 8);
            ctx.fillStyle = '#ff6622';
            ctx.beginPath();
            ctx.arc(0, 6, 5, 0, Math.PI * 2);
            ctx.fill();
            break;
        case 'scrap':
            ctx.fillStyle = color;
            ctx.save();
            ctx.rotate(0.3);
            ctx.fillRect(-12, -6, 18, 10);
            ctx.restore();
            ctx.save();
            ctx.rotate(-0.5);
            ctx.fillRect(-4, -10, 14, 8);
            ctx.restore();
            break;
        case 'chip':
            ctx.fillStyle = color;
            ctx.fillRect(-12, -12, 24, 24);
            ctx.fillStyle = '#0a1020';
            for (let i = -2; i <= 2; i++) {
                for (let j = -2; j <= 2; j++) {
                    if ((i + j + seed) % 3 === 0) ctx.fillRect(i * 4 - 2, j * 4 - 2, 3, 3);
                }
            }
            break;
        case 'drone':
            ctx.fillStyle = color;
            ctx.beginPath(); ctx.arc(0, 0, 10, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = '#fff';
            ctx.beginPath(); ctx.arc(3, -2, 3, 0, Math.PI * 2); ctx.fill();
            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            ctx.beginPath(); ctx.moveTo(-14, -8); ctx.lineTo(-10, 0); ctx.lineTo(-14, 8); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(14, -8); ctx.lineTo(10, 0); ctx.lineTo(14, 8); ctx.stroke();
            break;
        case 'armor':
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.moveTo(-12, -8); ctx.lineTo(12, -8); ctx.lineTo(14, 4);
            ctx.lineTo(0, 16); ctx.lineTo(-14, 4);
            ctx.closePath(); ctx.fill();
            ctx.strokeStyle = '#fff5';
            ctx.stroke();
            break;
        default:
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.arc(0, 0, 12, 0, Math.PI * 2);
            ctx.fill();
    }
}

function star(ctx, cx, cy, spikes, outer, inner) {
    let rot = -Math.PI / 2;
    const step = Math.PI / spikes;
    ctx.beginPath();
    for (let i = 0; i < spikes; i++) {
        ctx.lineTo(cx + Math.cos(rot) * outer, cy + Math.sin(rot) * outer);
        rot += step;
        ctx.lineTo(cx + Math.cos(rot) * inner, cy + Math.sin(rot) * inner);
        rot += step;
    }
    ctx.closePath();
}

function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

function seeded(seed) {
    let s = (seed * 9301 + 49297) % 233280;
    return () => {
        s = (s * 9301 + 49297) % 233280;
        return s / 233280;
    };
}

function lighten(hex, amt) {
    const c = hex.replace('#', '');
    if (c.length !== 6) return hex;
    const n = parseInt(c, 16);
    let r = Math.min(255, (n >> 16) + amt);
    let g = Math.min(255, ((n >> 8) & 0xff) + amt);
    let b = Math.min(255, (n & 0xff) + amt);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/** Draw a small planet icon for the star map */
export function getPlanetIcon(planet, size = 48) {
    const key = `planet|${planet.id}|${size}`;
    if (iconCache.has(key)) return iconCache.get(key);
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const ctx = c.getContext('2d');
    const cx = size / 2, cy = size / 2, r = size * 0.38;

    const g = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, 1, cx, cy, r);
    g.addColorStop(0, planet.colorLight || '#fff');
    g.addColorStop(0.5, planet.color);
    g.addColorStop(1, planet.colorDark || '#222');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();

    if (planet.hasRings) {
        ctx.strokeStyle = planet.ringColor || '#aa8866aa';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, r * 1.4, r * 0.35, -0.4, 0, Math.PI * 2);
        ctx.stroke();
    }

    // Atmosphere glow
    ctx.strokeStyle = (planet.colorLight || planet.color) + '66';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, r + 2, 0, Math.PI * 2);
    ctx.stroke();

    const url = c.toDataURL('image/png');
    iconCache.set(key, url);
    return url;
}
